import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { styled, type StyledComponentWithRef } from '@glitz/react';
import { animate } from './utils';

export function createAnimation(
  inKeyframe: (element: Element) => Omit<React.CSSProperties, 'offset'>,
  outKeyframe: Omit<React.CSSProperties, 'offset'>,
) {
  return <
    TProps extends React.HTMLAttributes<HTMLElement> | React.SVGAttributes<SVGElement>,
    TInstance extends Element,
  >(
    Component: StyledComponentWithRef<TProps, TInstance>,
    options?: KeyframeAnimationOptions,
  ) => {
    type PropType = {
      children?: React.ReactNode | undefined;
      visible?: boolean;
      keepElement?: boolean;
      initAnimation?: boolean;
      onAnimateInStart?: () => void;
      onAnimateInFinish?: (e: AnimationPlaybackEvent) => void;
      onAnimateOutStart?: () => void;
      onAnimateOutFinish?: (e: AnimationPlaybackEvent) => void;
    } & TProps;

    return styled(
      React.forwardRef<TInstance, PropType>(
        (
          {
            children,
            visible: externallyVisible = true,
            keepElement = false,
            initAnimation = false,
            onAnimateInStart,
            onAnimateInFinish,
            onAnimateOutStart,
            onAnimateOutFinish,
            ...restProps
          },
          ref,
        ) => {
          const [internallyVisible, setInternallyVisible] = React.useState(!initAnimation && externallyVisible);

          const elementRef = React.useRef<TInstance>(null);
          React.useImperativeHandle<TInstance | null, TInstance | null>(ref, () => elementRef.current);

          const animationEventHandlersRef =
            React.useRef<
              [typeof onAnimateInStart, typeof onAnimateInFinish, typeof onAnimateOutStart, typeof onAnimateOutFinish]
            >(null);
          animationEventHandlersRef.current = [
            onAnimateInStart,
            onAnimateInFinish,
            onAnimateOutStart,
            onAnimateOutFinish,
          ];

          if (__BROWSER__) {
            // Needs to be sync otherwise the content will flash
            // before it disappears and animates in
            // eslint-disable-next-line react-hooks/rules-of-hooks
            React.useLayoutEffect(() => {
              if (elementRef.current) {
                if (internallyVisible && !externallyVisible) {
                  const onAnimateOutStart = animationEventHandlersRef.current![2];
                  if (onAnimateOutStart) {
                    onAnimateOutStart();
                  }

                  const animation = animate(elementRef.current, [inKeyframe(elementRef.current), outKeyframe], options);
                  animation.onfinish = e => {
                    // flushSync forces react to set this state synchronously. This avoids flickering in react 18 new rendering engine.
                    ReactDOM.flushSync(() => {
                      setInternallyVisible(false);
                    });

                    const onAnimateOutFinish = animationEventHandlersRef.current![3];
                    if (onAnimateOutFinish) {
                      onAnimateOutFinish(e);
                    }
                  };
                }
                if (!internallyVisible && externallyVisible) {
                  const onAnimateInStart = animationEventHandlersRef.current![0];
                  if (onAnimateInStart) {
                    onAnimateInStart();
                  }

                  const animation = animate(elementRef.current, [outKeyframe, inKeyframe(elementRef.current)], options);
                  animation.onfinish = e => {
                    // flushSync forces react to set this state synchronously. This avoids flickering in react 18 new rendering engine.
                    ReactDOM.flushSync(() => {
                      setInternallyVisible(true);
                    });

                    const onAnimateInFinish = animationEventHandlersRef.current![1];
                    if (onAnimateInFinish) {
                      onAnimateInFinish(e);
                    }
                  };
                }
              }
            }, [internallyVisible, externallyVisible]);
          }

          const style = React.useMemo(
            () => ({
              ...(!(internallyVisible || externallyVisible) && keepElement && outKeyframe),
              ...restProps.style,
            }),
            [externallyVisible, internallyVisible, keepElement, restProps.style],
          );

          return internallyVisible || externallyVisible || keepElement ? (
            <Component {...(restProps as any)} style={style} ref={elementRef}>
              {children}
            </Component>
          ) : null;
        },
      ),
    );
  };
}

export const createFadeAnimationComponent = createAnimation(() => ({ opacity: 1 }), { opacity: 0 });
export const FadeAnimation = createFadeAnimationComponent(styled.Div);

export const createScaleAnimationComponent = createAnimation(() => ({ transform: 'scale(1)', opacity: 1 }), {
  transform: 'scale(0)',
  opacity: 0,
});
export const ScaleAnimation = createScaleAnimationComponent(styled.Div);

export const createSlideHeightAnimationComponent = createAnimation(
  element => {
    const { marginTop, marginBottom, paddingTop, paddingBottom } = getComputedStyle(element);
    return {
      height: `${element.scrollHeight}px`,
      opacity: 1,
      marginTop,
      marginBottom,
      paddingTop,
      paddingBottom,
      overflow: 'hidden',
    };
  },
  { height: 0, opacity: 0, marginTop: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0, overflow: 'hidden' },
);
export const SlideHeightAnimation = createSlideHeightAnimationComponent(styled.Div);
